import { useContext, useEffect, useRef } from 'react';
import { ReactFeedbackContext, type FeedbackContextApi, type FeedbackContextRecord } from './feedbackContextStore';

/** Read-only handle FeedbackPanel uses to merge every currently-registered page's context at
 * submit time. Returns null outside a FeedbackContextProvider (EC10: never throws). */
export function useFeedbackContextApi(): FeedbackContextApi | null {
  return useContext(ReactFeedbackContext);
}

let nextRegistrationId = 0;

/**
 * Registers page-contributed context (F6 — e.g. BankAccountDetails.tsx's `{ bankAccountId }`)
 * for the lifetime of the calling component. `extra` can be a fresh object literal on every
 * render — it's re-registered on every render (cheap: a Map.set of a handful of keys), never
 * gated behind a changed-values comparison that risks missing an update.
 */
export function useFeedbackContext(extra: FeedbackContextRecord): void {
  const registry = useContext(ReactFeedbackContext);
  const idRef = useRef<string | undefined>(undefined);
  if (idRef.current === undefined) {
    idRef.current = `feedback-context-${++nextRegistrationId}`;
  }

  useEffect(() => {
    if (!registry) return;
    const id = idRef.current as string;
    registry.register(id, extra);
    return () => registry.unregister(id);
  });
}
